// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useRef } from 'react';
import flags from 'react-phone-number-input/flags';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';

// All countries from libphonenumber-js, sorted by calling code
const phoneCountries: string[] = getCountries().sort((a, b) => {
  const codeA = getCountryCallingCode(a);
  const codeB = getCountryCallingCode(b);
  return Number(codeA) - Number(codeB);
});

export const DEFAULT_PHONE_COUNTRY = 'IE';
export { phoneCountries };

interface PhoneFlagDropdownProps {
  value: string;
  onSelect: (code: string, dialCode: string) => void;
}

export function PhoneFlagDropdown({ value, onSelect }: PhoneFlagDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const SelectedFlag = flags[value as keyof typeof flags];

  const handleSelect = (countryCode: string) => {
    const dialCode = getCountryCallingCode(countryCode as Parameters<typeof getCountryCallingCode>[0]);
    onSelect(countryCode, String(dialCode));
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={`h-[48px] px-4 border border-grey-04 bg-white flex items-center justify-between gap-4 cursor-pointer ${open ? 'rounded-t-[8px] border-b-0' : 'rounded-lg'}`}
        onClick={() => setOpen(!open)}
      >
        <span className="w-[28px] h-[20px]">
          {SelectedFlag && <SelectedFlag title={value} />}
        </span>
        <svg className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.0005 16.8006C11.3005 16.8006 10.6005 16.5306 10.0705 16.0006L3.55051 9.48062C3.26051 9.19062 3.26051 8.71062 3.55051 8.42063C3.84051 8.13063 4.32051 8.13063 4.61051 8.42063L11.1305 14.9406C11.6105 15.4206 12.3905 15.4206 12.8705 14.9406L19.3905 8.42063C19.6805 8.13063 20.1605 8.13063 20.4505 8.42063C20.7405 8.71062 20.7405 9.19062 20.4505 9.48062L13.9305 16.0006C13.4005 16.5306 12.7005 16.8006 12.0005 16.8006Z" fill="#141414"/>
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 z-50 w-full bg-white border border-grey-04 border-t-0 rounded-b-[8px] max-h-[240px] overflow-y-auto flex flex-col p-1">
          {phoneCountries.map(c => {
            const Flag = flags[c as keyof typeof flags];
            if (!Flag) return null;
            const code = getCountryCallingCode(c as Parameters<typeof getCountryCallingCode>[0]);
            return (
              <button
                key={c}
                type="button"
                className={`w-full h-[28px] px-3 rounded hover:bg-gray-100 flex items-center gap-2 shrink-0 ${c === value ? 'bg-blue-50 ring-1 ring-blue-500' : ''}`}
                onClick={() => handleSelect(c)}
                title={c}
              >
                <span className="w-[28px] h-[20px] flex-shrink-0"><Flag title={c} /></span>
                <span className="text-xs text-grey-04 font-sans">+{code}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Helper to get the default phone value with dial code */
export function getDefaultPhoneValue(countryCode: string = DEFAULT_PHONE_COUNTRY): string {
  return `+${getCountryCallingCode(countryCode as Parameters<typeof getCountryCallingCode>[0])}`;
}
